<template lang="pug">
  .container(:id='`class${classNumber}`')
    .column#startSection
      h2.blue.class-title.title-medium.width-100.m-bottom-3.m-top-2
        | {{ classPlan.name ? innerI18n.t('teacher.classWithName', {classNumber, name: classPlan.name}) : innerI18n.t('teacher.class', {classNumber}) }}
      .features-grid.m4-bottom-10(v-if='!forStudent && !isStudentView' :id='`organization${classNumber}`')
        .column
          h3.title-small.m4-bottom-5 {{ innerI18n.t('teacher.organization') }}
          .row(v-for='organization in classOrganization' v-if='organization.text')
            simple-svg.black-con.m-right-1(:filepath='organization.icon' :width='"18px"' :height='"18px"')
            p.text-normal {{ organization.text }}
        .column(v-if='!isTeacherTraining')
          h3.title-small.m4-bottom-5 {{ innerI18n.t('teacher.classDistribution') }}
          .row.middle
            img.class-layout.m-right-2(:src='classroomLayout' :alt='classLayoutDescription')
            h2.text-normal {{ classLayoutDescription }}
        .column
          h3.title-small.m4-bottom-5 {{ innerI18n.t('teacher.time') }}
          .row
            simple-svg.black-con.m-right-1(:filepath='Clock' :width='"18px"' :height='"18px"')
            p.time.text-normal {{ classPlan.minutesDuration }} {{ innerI18n.t('teacher.minutes') }}
    .class-content
      .features-grid(v-if='offlineContentLength' :id='`necessaryMaterials${classNumber}`')
        h3.item-1.title-medium.m4-bottom-10.mobile-title-size(v-if='offlineContentLength') {{ innerI18n.t('student.necessaryMaterial') }}
        .m-top-1.item-1.column.top.m-right-8.width-100.mobile-margin(v-if='offlineContentLength && electronicMaterials.length')
          h3.title-small.m-bottom-1 {{ innerI18n.t('student.devices') }}
          .row.middle.m4-bottom-5
            simple-svg.m-right-1.m-bottom-auto(:filepath='devicesIcon' width='18px' height='18px' alt='online material')
            span.text-normal {{ electronicMaterials }}
        .m-top-1.item-1.column.top.width-100(v-if='offlineContentLength && physicalMaterials.length')
          h3.title-small.m-bottom-1 {{ innerI18n.t('student.materials') }}
          .row.middle.m4-bottom-5
            simple-svg.m-right-1.m-bottom-auto(:filepath='materialsIcon' width='18px' height='18px' alt='offline material')
            span.text-normal {{ physicalMaterials }}
      .column(v-if='(forStudent || isStudentView) && studentStatement' :id='`assignament${classNumber}`')
        .column#statement
          h3.title-medium.m-bottom-1
            | {{ innerI18n.t('student.statement') }}
          .md-content.m-left-1(v-html='studentStatement')
      .column(v-else)
        #startSection.m-bottom-2(v-if='start')
          h3.title-medium.m-bottom-1
            | {{ innerI18n.t('teacher.start') }}
          .md-content.m-left-1(v-html='start')
        #developmentSection.m-bottom-2(v-if='development')
          h3.title-medium.m-bottom-1
            | {{ innerI18n.t('teacher.development') }}
          .md-content.m-left-1(v-html='development')
        #closureSection.m-bottom-2(v-if='closure')
          h3.title-medium.m-bottom-1
            | {{ innerI18n.t('teacher.closure') }}
          .md-content.m-left-1(v-html='closure')
      #valuationSection.m-top-3(v-if='!forStudent && !isStudentView && valuation')
        h3.title-medium.m-bottom-1
          | {{ innerI18n.t('teacher.valuation') }}
        .md-content.m-left-1(v-html='valuation')
      #necessaryDownloadableMaterials.column.top.item-1.m-top-13(v-if='classPlan.necessaryDownloadableMaterials.length')
        .row.m4-bottom-5
          img.material-icon.m-right-2(src='../../assets/ic_download.svg' alt='downloadable material')
          h1.title-medium
            | {{ innerI18n.t('teacher.downloadableMaterial') }}
        .material(v-for='material in classPlan.necessaryDownloadableMaterials')
          a.black.text-big(:href='material.link' target='_blank' rel='noreferrer noopener')
            | {{ material.description }}
          button.secondary-button.with-icon.mobile-hide(type='button' @click='downloadFileWithNewFilename(material.link, material.description)')
            simple-svg.blue-icon.m-right-1(:filepath='DownloadIcon' :width='"20px"' :height='"20px"')
            | {{ innerI18n.t('student.download') }}
          //- TODO: Add new button style for mobile view
          button.secondary-button.with-icon.mobile-show-only(type='button' @click='downloadFileWithNewFilename(material.link, material.description)')
            simple-svg.blue-icon(:filepath='DownloadIcon' :width='"18px"' :height='"18px"')
        .m-top-2.row.middle.end(v-if='classPlan.necessaryDownloadableMaterials.length > 1')
          button.secondary-button.with-icon.mobile-hide(@click='downloadAllClassFiles')
            simple-svg.blue-icon.m-right-1(:filepath='downloadIcon' width='20px' height='20px')
            | {{ innerI18n.t('teacher.downloadAll') }}
</template>

<script>
import MarkdownIt from 'markdown-it'
import { i18n, innerI18n } from '../../main'
import Group from '@/assets/group_layout.svg'
import Circle from '@/assets/circle_layout.svg'
import HalfCircle from '@/assets/half_circle_layout.svg'
import Row from '@/assets/row_layout.svg'
import Individual from '@/assets/ic_user_one.svg'
import SmallGroups from '@/assets/ic_user_two.svg'
import Multiple from '@/assets/ic_user_multiple.svg'
import devicesIcon from '@/assets/ic_devices.svg'
import materialsIcon from '@/assets/ic_materials.svg'
import Clock from '@/assets/ic_clock.svg'
import DownloadIcon from '@/assets/ic_download.svg'
import { downloadFileWithNewFilename, zipFilesFromUrls } from '../../utils/download'
import { mapState } from 'vuex'
import { routes } from '../../router'
import { toLowerCamelCase } from '../../utils/string'
import { teachersTrainingReferenceId } from '../../constants/resource'

export default {
  data() {
    return {
      materialsIcon,
      devicesIcon,
      classLayout: { Group, Circle, HalfCircle, Row },
      studentOrganization: { Individual, SmallGroups, Group: Multiple },
      md: new MarkdownIt({ html: true }),
      Clock,
      DownloadIcon,
      innerI18n,
      downloadFileWithNewFilename
    }
  },
  computed: {
    ...mapState({
      resource: state => state.resource.resource
    }),
    isTeacherTraining() {
      return this.resource.category.referenceId === teachersTrainingReferenceId
    },
    forStudent() {
      return this.$route.name === routes.studentResourceClass
    },
    classNumber() {
      return Number(this.$route.params.classId)
    },
    isStudentView() {
      return this.$route.meta.isStudentPublicResource
    },
    classPlan() {
      if (this.isStudentView) return this.resource.classPlans[this.classNumber - 1]
      else if (this.forStudent) return this.resource.studentMaterial.classPlans[this.classNumber - 1]
      else return this.resource.teacherMaterial.classPlans[this.classNumber - 1]
    },
    classroomLayout() {
      return this.classLayout[this.classPlan.classLayoutDescription]
    },
    classLayoutDescription() {
      return innerI18n.t(`teacher.classPlanLayout.${toLowerCamelCase(this.classPlan.classLayoutDescription)}`)
    },
    electronicMaterials() {
      return this.classPlan.necessaryElectronicMaterials.join(', ')
    },
    physicalMaterials() {
      return this.classPlan.necessaryPhysicalMaterials.join(', ')
    },
    start() {
      const shouldShowStart = !this.forStudent && this.classPlan.startSection
      return (shouldShowStart && this.md.render(this.classPlan.startSection)) || null
    },
    development() {
      const shouldShowDevelopment = !this.forStudent && this.classPlan.developmentSection
      return (shouldShowDevelopment && this.md.render(this.classPlan.developmentSection)) || null
    },
    closure() {
      const shouldShowClosure = !this.forStudent && this.classPlan.closureSection
      return (shouldShowClosure && this.md.render(this.classPlan.closureSection)) || null
    },
    valuation() {
      const shouldShowValuation = !this.forStudent && this.classPlan.valuationSection
      return (shouldShowValuation && this.md.render(this.classPlan.valuationSection)) || null
    },
    studentStatement() {
      const shouldShowStudentStatement = (this.forStudent || this.isStudentView) && this.classPlan.statement
      return (shouldShowStudentStatement && this.md.render(this.classPlan.statement)) || null
    },
    classOrganization() {
      const { organizationTypeDescription, secondaryOrganizationTypeDescription } = this.classPlan
      const classPlans = [
        {
          text: innerI18n.t(`teacher.classOrganization.${organizationTypeDescription.toLowerCase()}`),
          icon: this.studentOrganization[organizationTypeDescription]
        }
      ]
      if (secondaryOrganizationTypeDescription) {
        classPlans.push({
          text: this.$t(`teacher.classOrganization.${secondaryOrganizationTypeDescription.toLowerCase()}`),
          icon: this.studentOrganization[secondaryOrganizationTypeDescription]
        })
      }
      return classPlans
    },
    onlineContentLength() {
      return this.classPlan.necessaryDownloadableMaterials.length
    },
    offlineContentLength() {
      return this.classPlan.necessaryElectronicMaterials.length || this.classPlan.necessaryPhysicalMaterials.length
    }
  },
  methods: {
    downloadAllClassFiles() {
      const files = this.classPlan.necessaryDownloadableMaterials.map(file => ({ name: file.description, url: file.link }))
      zipFilesFromUrls(files, `${this.resource.title} - ${i18n.t('sidebar.class')} ${this.classNumber}`)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables/colors';
@import '../../scss/variables/sizes';

.features-grid{
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 20px;
}

.class-layout {
  width: 100%;
  max-width: 100px;
}

.class-title {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.mobile-show-only {
  display: none;
}

.material {
  align-items: center;
  border-bottom: 1px solid $light-gray;
  display: flex;
  justify-content: space-between;
  padding-bottom: 15px;
  width: 100%;

  &:not(:first-child) {
    margin-top: 15px;
  }
}

@media screen and (max-width: $mobile-break) {
  .mobile-hide {
    display: none;
  }

  .mobile-show-only {
    display: flex;
  }

  .container {
    margin: 0 20px;
  }

  .title-medium {
    font-size: 16px;
    font-weight: 600;
    line-height: 19px;
  }

  .text-big {
    font-size: 16px;
    line-height: 19px;
  }

  .class-materials {
    display: flex;
    flex-direction: row;
  }

  .row.mobile-column {
    flex-direction: column;
  }

  .self-end {
    align-self: flex-start;
  }

  .m-left-5 {
    margin-left: 40px;
  }

  .m-top-13 {
    margin-top: 20px;
  }

  .mobile-margin {
    margin-bottom: 30px;
  }

  .mobile-title-size {
    max-width: 100px;
  }

  .class-title {
    margin: 25px 0;
  }
}
</style>
